import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { addNotification } from '../../../store/uiSlice';
import apiService from '../../../services/api';

export interface ModelCatalogEntry {
  id: string;
  capabilities: Array<'text' | 'image' | 'video'>;
  tier: 'standard' | 'premium' | 'legacy' | string;
  pricing?: { unit: string; amount: number };
  label?: string;
}

export interface CurrentModelsPayload {
  defaults: Record<string, { model_id: string; updated_by?: string; updated_at?: string; updated_reason?: string }>;
  env: Record<string, string | null>;
  hardcoded: Record<string, string>;
}

export type ModelSlot =
  | 'text'
  | 'text_fast'
  | 'image'
  | 'video'
  | 'scene_analysis'
  | 'scene_analysis_fallback';

/**
 * useModelRegistry — loads the curated xAI model allow-list and the current
 * global defaults. Powers the ModelDefaultsBar on AI Styles and the video
 * dropdown on GrokImaginePromptsManagement. Mirrors useStylesData.handleRefresh
 * so admins can force-reload with the same mental model.
 */
export function useModelRegistry() {
  const dispatch = useDispatch<AppDispatch>();
  const [models, setModels] = useState<ModelCatalogEntry[]>([]);
  const [slots, setSlots] = useState<ModelSlot[]>([]);
  const [current, setCurrent] = useState<CurrentModelsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [stale, setStale] = useState<boolean>(false);
  const [syncAgeHours, setSyncAgeHours] = useState<number | null>(null);

  const applyAvailable = (availableData: any) => {
    setModels(availableData?.data || []);
    if (availableData?.slots) setSlots(availableData.slots as ModelSlot[]);
    setLastSyncedAt(availableData?.last_synced_at ?? null);
    setStale(Boolean(availableData?.stale));
    setSyncAgeHours(
      typeof availableData?.sync_age_hours === 'number' ? availableData.sync_age_hours : null
    );
  };

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const [available, currentResp] = await Promise.all([
        apiService.getAvailableModels(forceRefresh),
        apiService.getCurrentModels(),
      ]);
      applyAvailable(available as any);
      setCurrent(currentResp as CurrentModelsPayload);
    } catch (e: any) {
      console.error('[useModelRegistry] load failed', e);
      dispatch(addNotification({ type: 'error', message: e?.message || 'Failed to load model registry' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const refreshModels = useCallback(async () => {
    setUpdating(true);
    try {
      const refreshed = await apiService.refreshModels();
      applyAvailable(refreshed as any);
      const currentResp = await apiService.getCurrentModels();
      setCurrent(currentResp as CurrentModelsPayload);
      dispatch(addNotification({ type: 'success', message: 'Synced latest xAI model catalog' }));
    } catch (e: any) {
      const msg = e?.message || String(e);
      if (/429|RATE_LIMITED|too soon/i.test(msg)) {
        dispatch(addNotification({ type: 'warning', message: 'Refresh too soon — wait 30s' }));
      } else {
        dispatch(addNotification({ type: 'error', message: msg || 'Refresh failed' }));
      }
    } finally {
      setUpdating(false);
    }
  }, [dispatch]);

  const updateDefault = useCallback(async (slot: ModelSlot, modelId: string, reason?: string) => {
    setUpdating(true);
    try {
      await apiService.updateModelDefault(slot, modelId, reason);
      dispatch(addNotification({
        type: 'success',
        message: `Default for "${slot}" updated to ${modelId}. Propagation up to 60s.`,
      }));
      await load();
    } catch (e: any) {
      dispatch(addNotification({ type: 'error', message: e?.message || 'Failed to update default' }));
    } finally {
      setUpdating(false);
    }
  }, [dispatch, load]);

  const updatePricing = useCallback(async (modelId: string, pricingUnit: string, pricingAmount: number) => {
    setUpdating(true);
    try {
      await apiService.updateModelPricing(modelId, pricingUnit, pricingAmount);
      dispatch(addNotification({ type: 'success', message: `Pricing for "${modelId}" updated.` }));
      await load();
    } catch (e: any) {
      dispatch(addNotification({ type: 'error', message: e?.message || 'Failed to update pricing' }));
    } finally {
      setUpdating(false);
    }
  }, [dispatch, load]);

  const rollback = useCallback(async (slot: ModelSlot) => {
    setUpdating(true);
    try {
      await apiService.rollbackModel(slot);
      dispatch(addNotification({ type: 'success', message: `Rolled back "${slot}" to previous value` }));
      await load();
    } catch (e: any) {
      dispatch(addNotification({ type: 'error', message: e?.message || 'Rollback failed' }));
    } finally {
      setUpdating(false);
    }
  }, [dispatch, load]);

  useEffect(() => {
    load();
  }, [load]);

  const modelsByCapability = useCallback((capability: 'text' | 'image' | 'video') => {
    return models.filter(m => m.capabilities.includes(capability));
  }, [models]);

  const formatCost = (m: ModelCatalogEntry): string => {
    if (!m.pricing) return '';
    const unit = m.pricing.unit.includes('1M') ? '1M' : m.pricing.unit.includes('image') ? 'img' : m.pricing.unit.includes('second') ? 's' : m.pricing.unit;
    return `$${m.pricing.amount}/${unit}`;
  };

  const effectiveModel = useCallback((slot: ModelSlot): { value: string | null; source: 'db' | 'env' | 'hardcoded' | 'none' } => {
    if (!current) return { value: null, source: 'none' };
    const db = current.defaults[slot]?.model_id;
    if (db) return { value: db, source: 'db' };
    const env = current.env[slot];
    if (env) return { value: env, source: 'env' };
    const hc = current.hardcoded[slot];
    if (hc) return { value: hc, source: 'hardcoded' };
    return { value: null, source: 'none' };
  }, [current]);

  return {
    models,
    slots,
    current,
    loading,
    updating,
    lastSyncedAt,
    stale,
    syncAgeHours,
    refreshModels,
    updateDefault,
    rollback,
    modelsByCapability,
    formatCost,
    effectiveModel,
    updatePricing,
  };
}
